/**
 * PaiementForm — records a payment against an invoice (POST /paiements,
 * cap finance.paiement.collect, server-gated on an open caisse).
 *
 * Computes change-to-give (rendu) live from montant_tendu − montant. Keyboard-first; the last
 * recorded payment is echoed back read-only. `prefillFactureId` lets the facture screen hand off
 * the just-issued invoice for one-click collection.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Badge, Button, Card, FormField, Input, Select, useToast } from "@/ui";
import { useCan } from "@/auth";
import { useT } from "@/app";
import { ApiError } from "@/api";
import { FINANCE_CAPS, useCollectPaiement, useLabId } from "../api";
import type { Paiement, PaiementMode } from "../types";
import { MoneyInput } from "./MoneyInput";
import { Money } from "./Money";

const MODES: PaiementMode[] = ["especes", "carte", "cheque", "autre"];

export interface PaiementFormProps {
  sessionId: string;
  /** Hand-off from the facture screen: prefill the invoice id (+ amount) and focus the form. */
  prefillFactureId?: string;
  prefillMontant?: number | null;
}

export function PaiementForm({ sessionId, prefillFactureId, prefillMontant }: PaiementFormProps) {
  const { t } = useT();
  const toast = useToast();
  const labId = useLabId();
  const canCollect = useCan(FINANCE_CAPS.paiementCollect);
  const mutation = useCollectPaiement(labId, sessionId);

  const [factureId, setFactureId] = useState(prefillFactureId ?? "");
  const [montant, setMontant] = useState<number | null>(prefillMontant ?? null);
  const [tendu, setTendu] = useState<number | null>(prefillMontant ?? null);
  const [mode, setMode] = useState<PaiementMode>("especes");
  const [touched, setTouched] = useState(false);
  const [last, setLast] = useState<Paiement | null>(null);

  // Apply a fresh hand-off from the facture screen.
  useEffect(() => {
    if (prefillFactureId) {
      setFactureId(prefillFactureId);
      setMontant(prefillMontant ?? null);
      setTendu(prefillMontant ?? null);
      setTouched(false);
    }
  }, [prefillFactureId, prefillMontant]);

  const rendu = useMemo(() => {
    if (mode !== "especes") return 0;
    if (montant == null || tendu == null) return null;
    return Math.max(0, tendu - montant);
  }, [mode, montant, tendu]);

  const factureError = touched && factureId.trim() === "" ? t("finance.field.required") : null;
  const montantError = touched && (montant == null || montant <= 0) ? t("finance.field.amountInvalid") : null;
  const tenduTooLow =
    mode === "especes" && montant != null && tendu != null && tendu < montant;
  const tenduError =
    touched && (tendu == null || tendu <= 0)
      ? t("finance.field.amountInvalid")
      : tenduTooLow
        ? t("finance.paiement.tenduTooLow")
        : null;

  const valid =
    factureId.trim() !== "" &&
    montant != null &&
    montant > 0 &&
    tendu != null &&
    tendu > 0 &&
    !tenduTooLow;

  const modeOptions = MODES.map((m) => ({ value: m, label: t(`finance.paiement.mode.${m}`) }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || !canCollect) return;
    mutation.mutate(
      {
        facture_id: factureId.trim(),
        montant: montant ?? 0,
        montant_tendu: tendu ?? 0,
        mode,
      },
      {
        onSuccess: (data) => {
          toast.success(t("finance.paiement.success"));
          setLast(data);
          setFactureId("");
          setMontant(null);
          setTendu(null);
          setTouched(false);
        },
        onError: (err) => {
          toast.error(err instanceof ApiError ? err.message : t("finance.paiement.submit"));
        },
      },
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      <Card title={t("finance.paiement.title")}>
        <form onSubmit={submit} noValidate>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)", maxWidth: 480 }}>
            <FormField
              label={t("finance.paiement.factureId")}
              hint={t("finance.paiement.factureId.hint")}
              required
              {...(factureError ? { error: factureError } : {})}
            >
              {(p) => (
                <Input
                  {...p}
                  mono
                  className="nv-ltr-island"
                  autoComplete="off"
                  value={factureId}
                  invalid={!!factureError}
                  onChange={(e) => setFactureId(e.target.value)}
                />
              )}
            </FormField>

            <FormField
              label={t("finance.paiement.montant")}
              hint={t("finance.paiement.montant.hint")}
              required
              {...(montantError ? { error: montantError } : {})}
            >
              {(p) => (
                <MoneyInput
                  {...p}
                  aria-label={t("finance.amount.aria")}
                  valueCentimes={montant}
                  onChangeCentimes={(c) => {
                    setMontant(c);
                    // Keep tendu in lock-step for non-cash modes (no change to give).
                    if (mode !== "especes") setTendu(c);
                  }}
                  invalid={!!montantError}
                />
              )}
            </FormField>

            <FormField label={t("finance.paiement.mode")}>
              {(p) => (
                <Select
                  {...p}
                  options={modeOptions}
                  value={mode}
                  onChange={(e) => {
                    const next = e.target.value as PaiementMode;
                    setMode(next);
                    if (next !== "especes") setTendu(montant);
                  }}
                />
              )}
            </FormField>

            {mode === "especes" && (
              <FormField
                label={t("finance.paiement.tendu")}
                hint={t("finance.paiement.tendu.hint")}
                required
                {...(tenduError ? { error: tenduError } : {})}
              >
                {(p) => (
                  <MoneyInput
                    {...p}
                    aria-label={t("finance.amount.aria")}
                    valueCentimes={tendu}
                    onChangeCentimes={setTendu}
                    invalid={!!tenduError}
                  />
                )}
              </FormField>
            )}

            {mode === "especes" && rendu != null && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "var(--nv-s-3)",
                  borderRadius: "var(--nv-r-md)",
                  background: "var(--nv-surface-2)",
                }}
              >
                <span style={{ color: "var(--nv-slate)" }}>{t("finance.paiement.rendu")}</span>
                <span style={{ fontSize: "var(--nv-fs-title)" }}>
                  <Money centimes={rendu} strong />
                </span>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button type="submit" loading={mutation.isPending} disabled={!canCollect}>
                {t("finance.paiement.submit")}
              </Button>
            </div>
          </div>
        </form>
      </Card>

      {last && (
        <Card title={t("finance.paiement.last")}>
          <dl style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "var(--nv-s-2)", margin: 0 }}>
            <dt style={{ color: "var(--nv-slate)" }}>{t("finance.paiement.factureId")}</dt>
            <dd className="nv-mono nv-ltr-island" style={{ margin: 0, textAlign: "end" }}>
              {last.facture_id}
            </dd>
            <dt style={{ color: "var(--nv-slate)" }}>{t("finance.paiement.montant")}</dt>
            <dd style={{ margin: 0, textAlign: "end" }}>
              <Money centimes={last.montant} />
            </dd>
            <dt style={{ color: "var(--nv-slate)" }}>{t("finance.paiement.rendu")}</dt>
            <dd style={{ margin: 0, textAlign: "end" }}>
              <Money centimes={last.monnaie_rendue ?? Math.max(0, (last.montant_tendu ?? 0) - last.montant)} />
            </dd>
            <dt style={{ color: "var(--nv-slate)" }}>{t("finance.paiement.mode")}</dt>
            <dd style={{ margin: 0, textAlign: "end" }}>
              <Badge tone="neutral">{t(`finance.paiement.mode.${last.mode ?? "especes"}`)}</Badge>
            </dd>
          </dl>
        </Card>
      )}
    </div>
  );
}
